import { useEffect, useMemo, useState } from "react";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import { SYSTEM_VARIABLES } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";

const CASE_14_SPLIT_CONFIG_KEY = "CASE_14_SPLIT_CONFIG";
const CASE_14_PERCENT_SERASA_KEY = "CASE_14_PERCENT_SERASA";
const CASE_14_PERCENT_CREDIAVALIA_KEY = "CASE_14_PERCENT_CREDIAVALIA";
const SERASA_CLIENT_ID_KEY = "SERASA_CLIENT_ID";
const SERASA_CLIENT_SECRET_KEY = "SERASA_CLIENT_SECRET";

const SERASA_ALLOWED_KEYS = [
  CASE_14_SPLIT_CONFIG_KEY,
  CASE_14_PERCENT_SERASA_KEY,
  CASE_14_PERCENT_CREDIAVALIA_KEY,
  SERASA_CLIENT_ID_KEY,
  SERASA_CLIENT_SECRET_KEY,
];
const CASE_14_PROVIDER_IDS = ["14", "21"];

const buildProviderClientIdKey = (providerId) =>
  `SERASA_PROVIDER_${providerId}_CLIENT_ID`;
const buildProviderClientSecretKey = (providerId) =>
  `SERASA_PROVIDER_${providerId}_CLIENT_SECRET`;

const parsePercent = (value) => {
  const parsed = Number(String(value).replace(",", ".").trim());
  if (Number.isNaN(parsed)) return null;
  return Math.min(100, Math.max(0, parsed));
};

const normalizePair = (serasa, crediavalia) => {
  if (serasa === null && crediavalia === null) return null;

  let s = serasa;
  let c = crediavalia;

  if (s === null) s = 100 - c;
  if (c === null) c = 100 - s;

  const total = s + c;
  if (total <= 0) return null;

  return Math.round((s * 100) / total);
};

const parseSplitConfigPercent = (rawValue) => {
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue);

    if (!parsed || typeof parsed !== "object") return null;

    const hasDirect =
      Object.prototype.hasOwnProperty.call(parsed, "serasa") ||
      Object.prototype.hasOwnProperty.call(parsed, "crediavalia");

    if (hasDirect) {
      return normalizePair(
        parsePercent(parsed.serasa),
        parsePercent(parsed.crediavalia),
      );
    }

    // Se vier por PF/PJ e forem diferentes, usa PF como referência visual.
    if (parsed.pf && typeof parsed.pf === "object") {
      const pfPercent = normalizePair(
        parsePercent(parsed.pf.serasa),
        parsePercent(parsed.pf.crediavalia),
      );
      if (pfPercent !== null) return pfPercent;
    }

    if (parsed.pj && typeof parsed.pj === "object") {
      return normalizePair(
        parsePercent(parsed.pj.serasa),
        parsePercent(parsed.pj.crediavalia),
      );
    }

    return null;
  } catch (error) {
    return null;
  }
};

export default function SerasaSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [serasaPercent, setSerasaPercent] = useState(70);
  const [variables, setVariables] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [provider14ClientId, setProvider14ClientId] = useState("");
  const [provider14ClientSecret, setProvider14ClientSecret] = useState("");
  const [provider21ClientId, setProvider21ClientId] = useState("");
  const [provider21ClientSecret, setProvider21ClientSecret] = useState("");
  const [credentialsMessage, setCredentialsMessage] = useState("");

  const crediavaliaPercent = useMemo(
    () => 100 - serasaPercent,
    [serasaPercent],
  );

  const serasaVariables = useMemo(
    () =>
      variables.filter((item) => {
        if (SERASA_ALLOWED_KEYS.includes(item.key)) return true;
        return String(item.key || "")
          .toUpperCase()
          .includes("SERASA");
      }),
    [variables],
  );

  const loadCredentialsForm = (vars) => {
    const provider14ClientIdValue = vars.find(
      (item) => item.key === buildProviderClientIdKey("14"),
    )?.value;

    const provider14ClientSecretValue = vars.find(
      (item) => item.key === buildProviderClientSecretKey("14"),
    )?.value;

    const provider21ClientIdValue = vars.find(
      (item) => item.key === buildProviderClientIdKey("21"),
    )?.value;

    const provider21ClientSecretValue = vars.find(
      (item) => item.key === buildProviderClientSecretKey("21"),
    )?.value;

    const genericClientId = vars.find(
      (item) => item.key === SERASA_CLIENT_ID_KEY,
    )?.value;
    const genericClientSecret = vars.find(
      (item) => item.key === SERASA_CLIENT_SECRET_KEY,
    )?.value;

    setProvider14ClientId(provider14ClientIdValue || genericClientId || "");
    setProvider14ClientSecret(
      provider14ClientSecretValue || genericClientSecret || "",
    );
    setProvider21ClientId(provider21ClientIdValue || "");
    setProvider21ClientSecret(provider21ClientSecretValue || "");
  };

  const loadConfig = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const keysToLoad = [
        CASE_14_SPLIT_CONFIG_KEY,
        CASE_14_PERCENT_SERASA_KEY,
        CASE_14_PERCENT_CREDIAVALIA_KEY,
        SERASA_CLIENT_ID_KEY,
        SERASA_CLIENT_SECRET_KEY,
        buildProviderClientIdKey("14"),
        buildProviderClientSecretKey("14"),
        buildProviderClientIdKey("21"),
        buildProviderClientSecretKey("21"),
      ];

      const fetchGlobalVariable = async (key) => {
        try {
          const { data } = await axiosInstance.get(
            `${SYSTEM_VARIABLES.GET_BY_KEY}/${encodeURIComponent(key)}`,
          );

          return data?.data ? { key, value: data.data.value } : null;
        } catch (error) {
          if (error?.response?.status === 404) return null;
          throw error;
        }
      };

      const loaded = await Promise.all(keysToLoad.map(fetchGlobalVariable));
      const allVariables = loaded.filter(Boolean);
      setVariables(allVariables);
      loadCredentialsForm(allVariables);

      const splitJsonVariable = allVariables.find(
        (item) => item.key === CASE_14_SPLIT_CONFIG_KEY,
      );

      const splitPercentFromJson = parseSplitConfigPercent(
        splitJsonVariable?.value,
      );

      if (splitPercentFromJson !== null) {
        setSerasaPercent(splitPercentFromJson);
        return;
      }

      const serasaLegacy = allVariables.find(
        (item) => item.key === CASE_14_PERCENT_SERASA_KEY,
      );
      const crediavaliaLegacy = allVariables.find(
        (item) => item.key === CASE_14_PERCENT_CREDIAVALIA_KEY,
      );

      const normalizedLegacy = normalizePair(
        parsePercent(serasaLegacy?.value),
        parsePercent(crediavaliaLegacy?.value),
      );

      if (normalizedLegacy !== null) {
        setSerasaPercent(normalizedLegacy);
      }
    } catch (error) {
      console.error("Erro ao carregar configuração Serasa:", error);
      setErrorMessage("Não foi possível carregar as configurações do Serasa.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    const description =
      "Split de tráfego do case 14 entre Serasa e Serasa 2 (soma 100).";

    try {
      const splitConfigValue = JSON.stringify({
        serasa: serasaPercent,
        crediavalia: crediavaliaPercent,
      });

      const commonPayload = {
        description,
      };

      await axiosInstance.post(SYSTEM_VARIABLES.UPDATE, {
        ...commonPayload,
        key: CASE_14_SPLIT_CONFIG_KEY,
        value: splitConfigValue,
      });

      // Mantém legado sincronizado para compatibilidade de leitura.
      await axiosInstance.post(SYSTEM_VARIABLES.UPDATE, {
        ...commonPayload,
        key: CASE_14_PERCENT_SERASA_KEY,
        value: String(serasaPercent),
      });

      await axiosInstance.post(SYSTEM_VARIABLES.UPDATE, {
        ...commonPayload,
        key: CASE_14_PERCENT_CREDIAVALIA_KEY,
        value: String(crediavaliaPercent),
      });

      setSuccessMessage("Configuração do Serasa salva com sucesso.");
      await loadConfig();
    } catch (error) {
      console.error("Erro ao salvar configuração Serasa:", error);
      const apiMessage = error?.response?.data?.message;
      setErrorMessage(
        apiMessage || "Não foi possível salvar a configuração do Serasa.",
      );
    } finally {
      setSaving(false);
    }
  };

  const saveProviderCredentials = async () => {
    const credentialsByProvider = {
      14: {
        clientId: provider14ClientId,
        clientSecret: provider14ClientSecret,
      },
      21: {
        clientId: provider21ClientId,
        clientSecret: provider21ClientSecret,
      },
    };

    const hasInvalidPair = CASE_14_PROVIDER_IDS.some((provider) => {
      const creds = credentialsByProvider[provider];
      return !creds.clientId || !creds.clientSecret;
    });

    if (hasInvalidPair) {
      setCredentialsMessage(
        "Preencha client_id e client_secret para os dois providers (14 e 21).",
      );
      return;
    }

    setSavingCredentials(true);
    setCredentialsMessage("");

    try {
      for (const provider of CASE_14_PROVIDER_IDS) {
        const credentials = credentialsByProvider[provider];
        const description = `Credenciais Serasa do provider ${provider}.`;
        const commonPayload = {
          description,
        };

        await axiosInstance.post(SYSTEM_VARIABLES.UPDATE, {
          ...commonPayload,
          key: buildProviderClientIdKey(provider),
          value: credentials.clientId,
        });

        await axiosInstance.post(SYSTEM_VARIABLES.UPDATE, {
          ...commonPayload,
          key: buildProviderClientSecretKey(provider),
          value: credentials.clientSecret,
        });
      }

      setCredentialsMessage(
        "Credenciais dos providers 14 e 21 salvas com sucesso.",
      );
      await loadConfig();
    } catch (error) {
      console.error("Erro ao salvar credenciais do provider Serasa:", error);
      const apiMessage = error?.response?.data?.message;
      setCredentialsMessage(
        apiMessage || "Não foi possível salvar as credenciais do provider.",
      );
    } finally {
      setSavingCredentials(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-gray-50 p-5">
        <h2 className="text-lg font-semibold text-gray-900">Split do Serasa</h2>
        <p className="mt-1 text-sm text-gray-600">
          Defina a proporção de tráfego entre provedores do Serasa
        </p>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <Label value="Serasa (%)" />
            <TextInput value={String(serasaPercent)} readOnly />
          </div>

          <div>
            <Label value="Serasa 2 (%)" />
            <TextInput value={String(crediavaliaPercent)} readOnly />
          </div>
        </div>

        <div className="mt-6">
          <Label
            htmlFor="split-range"
            value="Distribuição Serasa"
          />
          <input
            id="split-range"
            type="range"
            min={0}
            max={100}
            step={1}
            value={serasaPercent}
            onChange={(event) => setSerasaPercent(Number(event.target.value))}
            className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-500">
            <span>0/100</span>
            <span>50/50</span>
            <span>100/0</span>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button onClick={saveConfig} isProcessing={saving}>
            Salvar configuração
          </Button>
          {successMessage ? (
            <span className="text-sm text-green-700">{successMessage}</span>
          ) : null}
          {errorMessage ? (
            <span className="text-sm text-red-700">{errorMessage}</span>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border bg-gray-50 p-5">
        <h2 className="text-lg font-semibold text-gray-900">
          Credenciais por Provedor Serasa
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Configure duas credenciais: provedor 1 e provedor 2.
        </p>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <Label
              htmlFor="provider-14-client-id"
              value="Provedor 1 - Client ID"
            />
            <TextInput
              id="provider-14-client-id"
              value={provider14ClientId}
              onChange={(event) => setProvider14ClientId(event.target.value)}
              placeholder="Informe o client_id"
            />
          </div>

          <div>
            <Label
              htmlFor="provider-14-client-secret"
              value="Provedor 1 - Client Secret"
            />
            <TextInput
              id="provider-14-client-secret"
              type="password"
              value={provider14ClientSecret}
              onChange={(event) =>
                setProvider14ClientSecret(event.target.value)
              }
              placeholder="Informe o client_secret"
            />
          </div>

          <div>
            <Label
              htmlFor="provider-21-client-id"
              value="Provedor 2 - Client ID"
            />
            <TextInput
              id="provider-21-client-id"
              value={provider21ClientId}
              onChange={(event) => setProvider21ClientId(event.target.value)}
              placeholder="Informe o client_id"
            />
          </div>

          <div>
            <Label
              htmlFor="provider-21-client-secret"
              value="Provedor 2 - Client Secret"
            />
            <TextInput
              id="provider-21-client-secret"
              type="password"
              value={provider21ClientSecret}
              onChange={(event) =>
                setProvider21ClientSecret(event.target.value)
              }
              placeholder="Informe o client_secret"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button
            onClick={saveProviderCredentials}
            isProcessing={savingCredentials}
          >
            Salvar credenciais dos dois providers
          </Button>
          {credentialsMessage ? (
            <span className="text-sm text-gray-700">{credentialsMessage}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
